import { Horizontal, MaterialIcon, Texto, Vertical } from '@gravitate-js/excalibrr'
import type {
  CellStyle,
  CellStyleFunc,
  CheckboxSelectionCallbackParams,
  ColDef,
  ICellRendererParams,
  ValueGetterParams,
} from 'ag-grid-community'
import type { Match, Warning } from '../../../../../../api/useSupplierReconciliation/types/match'
import { BolNumbersCellRenderer } from '../renderers/BolNumbersCellRenderer'
import { InvoiceNumbersCellRenderer } from '../renderers/InvoiceNumbersCellRenderer'
import { OrderNumbersCellRenderer } from '../renderers/OrderNumbersCellRenderer'
import { PoNumbersCellRenderer } from '../renderers/PoNumbersCellRenderer'
import { StatusCellRenderer } from '../renderers/StatusCellRenderer'
import { WarningsCellRenderer } from '../renderers/WarningsCellRenderer'
import {
  createBolStatusValueGetter,
  createDualColumnFilter,
  createDualColumnFilterParams,
  createInvoiceStatusValueGetter,
  createOrderStatusValueGetter,
  createPoStatusValueGetter,
} from '../utils/filterUtils'

interface ServerParams {
  params?: any[]
}

function deriveStatusDisplayValue(match: Match): string | null {
  if (!match?.status) return null

  // For unmatched status, determine if it's invoice or BOL specific
  if (match.status === 'unmatched') {
    const hasInvoiceIds = match.invoice_numbers && match.invoice_numbers.some((inv: any) => inv.id)
    const hasBolIds = match.bol_numbers && match.bol_numbers.some((bol: any) => bol.id)

    if (hasInvoiceIds && !hasBolIds) {
      return 'Unmatched Invoice'
    } else if (!hasInvoiceIds && hasBolIds) {
      return 'Unmatched BOL'
    } else if (hasInvoiceIds && hasBolIds) {
      return 'Unmatched'
    } else {
      // No IDs at all - this shouldn't happen but return basic unmatched
      return 'Unmatched'
    }
  }

  const statusMap: Record<string, string> = {
    unapproved: 'Unapproved',
    approved: 'Approved',
    hold: 'On Hold',
    void: 'Voided',
  }

  return statusMap[match.status] || match.status
}

function getFilterOptionsFromServerParams(serverParams?: ServerParams, fieldName?: string): string[] | undefined {
  if (!serverParams?.params || !fieldName) return undefined

  // Find the matching param by field name or title
  const matchingParam = serverParams.params.find(
    (param: any) =>
      param.filter_column === fieldName ||
      param.title?.toLowerCase() === fieldName.toLowerCase() ||
      param.title?.toLowerCase().includes(fieldName.toLowerCase())
  )

  if (matchingParam?.options && matchingParam.datatype === 'dropdown') {
    return matchingParam.options.map((option: any) => option.value || option.label || option)
  }

  return undefined
}

export function getBasicColumns(serverParams?: ServerParams): ColDef<Match>[] {
  return [
    // Checkbox selection column
    {
      colId: 'checkbox_selection',
      headerName: '',
      width: 50,
      checkboxSelection: (params: CheckboxSelectionCallbackParams<Match>) => {
        return params.node && !params.node.group
      },
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      resizable: false,
      sortable: false,
      filter: false,
      pinned: 'left',
    },

    // Supplier column
    {
      colId: 'supplier_name',
      headerName: 'SUPPLIERS',
      field: 'suppliers',
      filter: 'agSetColumnFilter',
      filterParams: {
        values: getFilterOptionsFromServerParams(serverParams, 'suppliers'),
        suppressSelectAll: false,
        suppressMiniFilter: false,
      },
      flex: 1.5,

      cellRenderer: ({ data }: ICellRendererParams<Match>) => {
        if (!data?.suppliers?.length) return null

        return (
          <Vertical verticalCenter>
            {data?.suppliers.map((x) => (
              <Horizontal style={{ overflow: 'visible' }} gap={8} verticalCenter>
                <MaterialIcon icon='store' style={{ color: 'var(--theme-color-2)' }} />
                <Texto>{x}</Texto>
              </Horizontal>
            ))}
          </Vertical>
        )
      },
      valueGetter: (params: ValueGetterParams<Match>) => {
        if (!params.data?.suppliers || !Array.isArray(params.data.suppliers)) return ''
        return params.data.suppliers.join(', ')
      },
      filterValueGetter: (params) => {
        if (!params.data?.suppliers || !Array.isArray(params.data.suppliers)) return null
        return params.data.suppliers
      },
    },

    // Warnings column (FLAGS)
    {
      colId: 'warnings_flags',
      headerName: 'FLAGS',
      width: 80,
      cellRenderer: WarningsCellRenderer,
      sortable: false,
      filter: 'agSetColumnFilter',
      resizable: false,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        overflow: 'visible',
      } as CellStyle,
      valueGetter: (params: ValueGetterParams<Match>) => {
        if (!params.data?.warnings?.length) return 'No Warnings'

        // Filter out warnings that are shown in BOL column instead
        const displayWarnings = params.data.warnings.filter((w: Warning) => {
          return w.type !== 'movement_validation' && w.type !== 'delivery_reconciliation'
        })

        if (displayWarnings.length === 0) return 'No Warnings'

        return displayWarnings
          .map((w: Warning) => {
            // Convert warning type to readable format
            switch (w.type) {
              case 'by_total_amount':
                return 'Total Amount Mismatch'
              case 'by_total_gross_volume':
                return 'Total Gross Volume Mismatch'
              case 'by_total_net_volume':
                return 'Total Net Volume Mismatch'
              case 'by_total_per_unit':
                return 'Total Per Unit Mismatch'
              case 'by_total_product_cost':
                return 'Total Product Cost Mismatch'
              case 'by_tax_total':
                return 'Tax Total Mismatch'
              case 'by_terminal':
                return 'Terminal Mismatch'
              case 'by_bol_date':
                return 'BOL Date Mismatch'
              default:
                return w.type
                  .replace(/^by_/, '')
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, (l) => l.toUpperCase())
            }
          })
          .join(', ')
      },
      filterValueGetter: (params: ValueGetterParams<Match>) => {
        if (!params.data?.warnings?.length) return 'No Warnings'

        // Filter out warnings that are shown in BOL column instead
        const displayWarnings = params.data.warnings.filter((w: Warning) => {
          return w.type !== 'movement_validation' && w.type !== 'delivery_reconciliation'
        })

        if (displayWarnings.length === 0) return 'No Warnings'

        // Return each warning type as a separate filter option
        const warningTypes = displayWarnings.map((w: Warning) => {
          switch (w.type) {
            case 'by_total_amount':
              return 'Total Amount Mismatch'
            case 'by_total_gross_volume':
              return 'Total Gross Volume Mismatch'
            case 'by_total_net_volume':
              return 'Total Net Volume Mismatch'
            case 'by_total_per_unit':
              return 'Total Per Unit Mismatch'
            case 'by_total_product_cost':
              return 'Total Product Cost Mismatch'
            case 'by_tax_total':
              return 'Tax Total Mismatch'
            case 'by_terminal':
              return 'Terminal Mismatch'
            case 'by_bol_date':
              return 'BOL Date Mismatch'
            default:
              return w.type
                .replace(/^by_/, '')
                .replace(/_/g, ' ')
                .replace(/\b\w/g, (l) => l.toUpperCase())
          }
        })

        return warningTypes
      },
    },

    // Status column
    {
      colId: 'match_status',
      headerName: 'STATUS',
      field: 'status',
      filter: 'agSetColumnFilter',
      flex: 1.5,
      cellRenderer: StatusCellRenderer,
      filterParams: {
        suppressSelectAll: false,
        suppressMiniFilter: false,
      },
      // Use filterValueGetter for filtering - shows "Unmatched Invoice", "Unmatched BOL" in dropdowns
      filterValueGetter: (params: ValueGetterParams<Match>) => {
        if (!params.data?.status) return null

        // For unmatched status, determine if it's invoice or BOL specific
        if (params.data.status === 'unmatched') {
          const hasInvoiceIds = params.data.invoice_numbers && params.data.invoice_numbers.some((inv: any) => inv.id)
          const hasBolIds = params.data.bol_numbers && params.data.bol_numbers.some((bol: any) => bol.id)

          if (hasInvoiceIds && !hasBolIds) {
            return 'Unmatched Invoice'
          } else if (!hasInvoiceIds && hasBolIds) {
            return 'Unmatched BOL'
          } else if (hasInvoiceIds && hasBolIds) {
            return 'Unmatched'
          } else {
            // No IDs at all - this shouldn't happen but return basic unmatched
            return 'Unmatched'
          }
        }

        const statusMap: Record<string, string> = {
          unapproved: 'Unapproved',
          approved: 'Approved',
          hold: 'On Hold',
          void: 'Voided',
        }

        return statusMap[params.data.status] || params.data.status
      },
      // Add valueGetter for grouping - shows "Unmatched Invoice", "Unmatched BOL" as separate groups
      valueGetter: (params: ValueGetterParams<Match>) => {
        return params.data ? deriveStatusDisplayValue(params.data) : null
      },
    },

    // Invoice Numbers column
    {
      colId: 'invoice_numbers',
      headerName: 'INVOICE #',
      flex: 1,
      cellRenderer: InvoiceNumbersCellRenderer,
      filter: createDualColumnFilter({
        field: 'invoice_numbers',
        numberFieldTitle: 'Filter By Invoice Number',
        statusFieldTitle: 'Filter By Status',
        valueGetter: (params: ValueGetterParams<Match>) => {
          if (!params.data?.invoice_numbers || !Array.isArray(params.data.invoice_numbers)) return ''
          return params.data.invoice_numbers
            .map((invoice: { id: string; number: string; source?: string }) => invoice.number)
            .join(', ')
        },
        statusValueGetter: createInvoiceStatusValueGetter(),
      }),
      filterParams: createDualColumnFilterParams({
        field: 'invoice_numbers',
        numberFieldTitle: 'Filter By Invoice Number',
        statusFieldTitle: 'Filter By Status',
        valueGetter: (params: ValueGetterParams<Match>) => {
          if (!params.data?.invoice_numbers || !Array.isArray(params.data.invoice_numbers)) return ''
          return params.data.invoice_numbers
            .map((invoice: { id: string; number: string; source?: string }) => invoice.number)
            .join(', ')
        },
        statusValueGetter: createInvoiceStatusValueGetter(),
      }),
      valueGetter: (params: ValueGetterParams<Match>) => {
        if (!params.data?.invoice_numbers || !Array.isArray(params.data.invoice_numbers)) return ''
        return params.data.invoice_numbers
          .map((invoice: { id: string; number: string; source?: string }) => invoice.number)
          .join(', ')
      },
      cellStyle: ((params: ICellRendererParams) => {
        if (!params.data) return {}

        const hasInvoices = params.data.invoice_numbers && params.data.invoice_numbers.length > 0
        const hasBols = params.data.bol_numbers && params.data.bol_numbers.length > 0

        if (!hasInvoices) {
          return { backgroundColor: 'var(--theme-error-dim)' }
        }

        if (hasInvoices && hasBols && params.data.has_variances) {
          return { color: 'var(--theme-warning)' }
        }

        return {}
      }) as CellStyleFunc<Match>,
      getQuickFilterText: (params) => {
        if (!params.data) return ''

        let invoiceNumbers = ''
        if (params.data?.invoice_numbers && Array.isArray(params.data.invoice_numbers)) {
          invoiceNumbers = params.data.invoice_numbers
            .map((invoice: { id: string; number: string; source?: string }) => invoice.number)
            .join(', ')
        }

        const status = params.data?.invoice_numbers && params.data.invoice_numbers.length > 0 ? 'Exists' : 'Missing'

        return `${invoiceNumbers} ${status}`
      },
    },

    // BOL Numbers column
    {
      colId: 'bol_numbers',
      headerName: 'BOL #',
      flex: 1,
      autoHeight: true,
      cellRenderer: BolNumbersCellRenderer,
      filter: createDualColumnFilter({
        field: 'bol_numbers',
        numberFieldTitle: 'Filter By BOL Number',
        statusFieldTitle: 'Filter By Status',
        valueGetter: (params: ValueGetterParams<Match>) => {
          if (!params.data?.bol_numbers || !Array.isArray(params.data.bol_numbers)) return ''
          return params.data.bol_numbers.map((bol: { id: string; number: string }) => bol.number).join(' ')
        },
        statusValueGetter: createBolStatusValueGetter(),
      }),
      filterParams: createDualColumnFilterParams({
        field: 'bol_numbers',
        numberFieldTitle: 'Filter By BOL Number',
        statusFieldTitle: 'Filter By Status',
        valueGetter: (params: ValueGetterParams<Match>) => {
          if (!params.data?.bol_numbers || !Array.isArray(params.data.bol_numbers)) return ''
          return params.data.bol_numbers.map((bol: { id: string; number: string }) => bol.number).join(' ')
        },
        statusValueGetter: createBolStatusValueGetter(),
      }),
      valueGetter: (params: ValueGetterParams<Match>) => {
        if (!params.data?.bol_numbers || !Array.isArray(params.data.bol_numbers)) return ''
        return params.data.bol_numbers.map((bol: { id: string; number: string }) => bol.number).join(' ')
      },
      getQuickFilterText: (params) => {
        if (!params.data) return ''

        let bolNumbers = ''
        if (params.data?.bol_numbers && Array.isArray(params.data.bol_numbers)) {
          bolNumbers = params.data.bol_numbers.map((bol: { id: string; number: string }) => bol.number).join(' ')
        }

        const statuses = []

        if (!params.data?.bol_numbers || !Array.isArray(params.data.bol_numbers)) {
          statuses.push('Missing')
        } else {
          const bolNumbersData = params.data.bol_numbers

          const hasMovementValidationError = params.data.warnings?.some(
            (w: Warning) => w.type === 'movement_validation'
          )
          if (hasMovementValidationError) {
            statuses.push('Order Movement Validations')
          }

          const hasDeliveryReconciliationError = params.data.warnings?.some(
            (w: Warning) => w.type === 'delivery_reconciliation'
          )
          if (hasDeliveryReconciliationError) {
            statuses.push('Delivery Reconciliation Errors')
          }

          const hasNullBolIds = bolNumbersData.some((bol: { id: string; number: string }) => !bol.id)
          if (hasNullBolIds) {
            statuses.push('Expected')
          }

          const hasValidBols = bolNumbersData.some((bol: { id: string; number: string }) => bol.id)
          if (hasValidBols) {
            statuses.push('Exists')
          }
        }

        const statusText = statuses.join(' ')

        return `${bolNumbers} ${statusText}`
      },
      cellStyle: ((params: ICellRendererParams) => {
        if (!params.data) return {}

        const hasBols = params.data.bol_numbers && params.data.bol_numbers.length > 0
        const hasInvoices = params.data.invoice_numbers && params.data.invoice_numbers.length > 0

        const hasMovementValidationError = params.data.warnings?.some((w: Warning) => w.type === 'movement_validation')

        const hasDeliveryReconciliationError = params.data.warnings?.some(
          (w: Warning) => w.type === 'delivery_reconciliation'
        )

        // Always show error styling for delivery reconciliation errors (regardless of blocking mode)
        // Only the approval functionality should be affected by blocking mode, not the visual warnings
        if (hasMovementValidationError || hasDeliveryReconciliationError) {
          return {
            backgroundColor: 'var(--theme-error)',
            color: 'white',
          }
        }

        if (!hasBols) {
          return { backgroundColor: 'var(--theme-error-dim)' }
        }

        const hasNullBolIds = params.data.bol_numbers.some((bol: { id: string; number: string }) => !bol.id)
        if (hasNullBolIds) {
          return { backgroundColor: 'var(--theme-error-dim)' }
        }

        if (hasBols && hasInvoices && params.data.has_variances) {
          return { color: 'var(--theme-warning)' }
        }

        return {}
      }) as CellStyleFunc<Match>,
    },
    {
      field: 'bol_carrier',
      headerName: 'BOL CARRIER',
      hide: true,
      cellRenderer: ({ data }: ICellRendererParams<Match>) => (
        <Texto appearance={data?.bol_carrier ? 'default' : 'hint'}>{data?.bol_carrier ?? 'N/A'}</Texto>
      ),
    },
    {
      colId: 'order_numbers',
      headerName: 'ORDER #',
      flex: 1,
      hide: true,
      cellRenderer: OrderNumbersCellRenderer,
      filter: createDualColumnFilter({
        field: 'order_numbers',
        numberFieldTitle: 'Filter By Order Number',
        statusFieldTitle: 'Filter By Status',
        valueGetter: (params: ValueGetterParams<Match>) => {
          if (!params.data?.order_numbers || !Array.isArray(params.data.order_numbers)) return ''
          return params.data.order_numbers.map((order: { id: string; number: string }) => order.number).join(' ')
        },
        statusValueGetter: createOrderStatusValueGetter(),
      }),
      filterParams: createDualColumnFilterParams({
        field: 'order_numbers',
        numberFieldTitle: 'Filter By Order Number',
        statusFieldTitle: 'Filter By Status',
        valueGetter: (params: ValueGetterParams<Match>) => {
          if (!params.data?.order_numbers || !Array.isArray(params.data.order_numbers)) return ''
          return params.data.order_numbers.map((order: { id: string; number: string }) => order.number).join(' ')
        },
        statusValueGetter: createOrderStatusValueGetter(),
      }),
      valueGetter: (params: ValueGetterParams<Match>) => {
        if (!params.data?.order_numbers || !Array.isArray(params.data.order_numbers)) return ''
        return params.data.order_numbers.map((order: { id: string; number: string }) => order.number).join(' ')
      },
    },

    // PO Numbers column
    {
      colId: 'po_numbers',
      headerName: 'PO #',
      flex: 1,
      hide: true,
      cellRenderer: PoNumbersCellRenderer,
      filter: createDualColumnFilter({
        field: 'po_numbers',
        numberFieldTitle: 'Filter By PO Number',
        statusFieldTitle: 'Filter By Status',
        valueGetter: (params: ValueGetterParams<Match>) => {
          if (!params.data?.po_numbers || !Array.isArray(params.data.po_numbers)) return ''
          return params.data.po_numbers.join(' ')
        },
        statusValueGetter: createPoStatusValueGetter(),
      }),
      filterParams: createDualColumnFilterParams({
        field: 'po_numbers',
        numberFieldTitle: 'Filter By PO Number',
        statusFieldTitle: 'Filter By Status',
        valueGetter: (params: ValueGetterParams<Match>) => {
          if (!params.data?.po_numbers || !Array.isArray(params.data.po_numbers)) return ''
          return params.data.po_numbers.join(' ')
        },
        statusValueGetter: createPoStatusValueGetter(),
      }),
      valueGetter: (params: ValueGetterParams<Match>) => {
        if (!params.data?.po_numbers || !Array.isArray(params.data.po_numbers)) return ''
        return params.data.po_numbers.join(' ')
      },
    },
  ]
}
